import { useState, useEffect } from 'react'
import { useAccount } from 'wagmi'
import { Link } from 'react-router-dom'
import { ExternalLink, Clock, CheckCircle, XCircle, ArrowLeftRight, Loader2, RefreshCw, Ban, AlertTriangle, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/i18n'
import { useUserBridges, LockStatus } from '@/hooks/useUserBridges'
import { useCancelBridge } from '@/hooks/useCancelBridge'
import { UNI2_EXPLORER, BSC_EXPLORER } from '@/constants/addresses'
import { api } from '@/lib/api'

export function HistoryPage() {
  const { isConnected, address } = useAccount()
  const { t, language } = useLanguage()
  const { locks, isLoading, refetch } = useUserBridges()
  const { cancelBridge, isPending: isCancelling, reset: resetCancel } = useCancelBridge()
  const [cancellingLockId, setCancellingLockId] = useState<bigint | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [lockToCancel, setLockToCancel] = useState<{ lockId: bigint; amount: string } | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [cancelledAmount, setCancelledAmount] = useState<string | null>(null)
  // Rejection reason modal state
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState<string | null>(null)
  const [isLoadingReason, setIsLoadingReason] = useState(false)
  // Source tx hash cache
  const [sourceTxHashes, setSourceTxHashes] = useState<Map<string, string>>(new Map())

  // Fetch source tx hashes for all locks
  useEffect(() => {
    if (!address || locks.length === 0) return

    const fetchSourceTxHashes = async () => {
      const newHashes = new Map<string, string>()

      for (const lock of locks) {
        const lockIdStr = lock.lockId.toString()
        // Skip if already cached
        if (sourceTxHashes.has(lockIdStr)) {
          newHashes.set(lockIdStr, sourceTxHashes.get(lockIdStr)!)
          continue
        }

        try {
          const result = await api.getSourceTxHash(lockIdStr, address)
          if (result.found && result.sourceTxHash) {
            newHashes.set(lockIdStr, result.sourceTxHash)
          }
        } catch (error) {
          console.error(`Error fetching source tx for lock ${lockIdStr}:`, error)
        }
      }

      setSourceTxHashes(newHashes)
    }

    fetchSourceTxHashes()
  }, [address, locks])

  const getStatusConfig = (status: LockStatus) => {
    switch (status) {
      case LockStatus.PENDING:
        return { icon: Clock, color: 'text-yellow-400', label: t('pending'), bgColor: 'bg-yellow-500/15 border-yellow-500/20' }
      case LockStatus.APPROVED:
        return { icon: Clock, color: 'text-blue-400', label: t('approved'), bgColor: 'bg-blue-500/15 border-blue-500/20' }
      case LockStatus.COMPLETED:
        return { icon: CheckCircle, color: 'text-accent-400', label: t('completed'), bgColor: 'bg-accent-500/15 border-accent-500/20' }
      case LockStatus.REJECTED:
        return { icon: Ban, color: 'text-red-400', label: t('rejected'), bgColor: 'bg-red-500/15 border-red-500/20' }
      case LockStatus.CANCELLED:
        return { icon: XCircle, color: 'text-dark-400', label: t('cancelled'), bgColor: 'bg-dark-500/15 border-dark-500/20' }
      default:
        return { icon: Clock, color: 'text-yellow-400', label: t('pending'), bgColor: 'bg-yellow-500/15 border-yellow-500/20' }
    }
  }

  const openCancelModal = (lockId: bigint, amount: string) => {
    setLockToCancel({ lockId, amount })
    setShowCancelModal(true)
  }

  const closeCancelModal = () => {
    setShowCancelModal(false)
    setLockToCancel(null)
  }

  // Sync cancellation with backend with retries
  const syncCancellationWithRetry = async (lockId: string, attempt: number = 1): Promise<boolean> => {
    const MAX_RETRIES = 3
    try {
      await api.syncCancellation(lockId)
      return true
    } catch (error) {
      console.error(`Failed to sync cancellation (attempt ${attempt}/${MAX_RETRIES}):`, error)
      if (attempt < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
        return syncCancellationWithRetry(lockId, attempt + 1)
      }
      return false
    }
  }

  const handleConfirmCancel = async () => {
    if (!lockToCancel) return

    const amountToCancel = lockToCancel.amount
    setCancellingLockId(lockToCancel.lockId)
    closeCancelModal()

    try {
      await cancelBridge(lockToCancel.lockId)
      // Sync cancellation with backend (with retries)
      await syncCancellationWithRetry(lockToCancel.lockId.toString())

      // Show success modal
      setCancelledAmount(amountToCancel)
      setShowSuccessModal(true)

      // Wait a moment then refetch to ensure blockchain state is updated
      setTimeout(() => {
        refetch()
      }, 1000)
    } catch (error) {
      console.error('Cancel error:', error)
      toast.error(t('error'))
    } finally {
      setCancellingLockId(null)
      resetCancel()
    }
  }

  const closeSuccessModal = () => {
    setShowSuccessModal(false)
    setCancelledAmount(null)
    refetch() // Refetch again when closing to ensure UI is updated
  }

  const fetchRejectionReason = async (lockId: bigint) => {
    if (!address) return

    setIsLoadingReason(true)
    try {
      const result = await api.getRejectionReason(lockId.toString(), address)
      if (result.hasReason && result.reason) {
        setRejectionReason(result.reason)
        setShowRejectionModal(true)
      } else {
        toast.error(language === 'es' ? 'No hay motivo disponible' : 'No reason available')
      }
    } catch (error) {
      console.error('Error fetching rejection reason:', error)
      toast.error(language === 'es' ? 'Error al obtener el motivo' : 'Error fetching reason')
    } finally {
      setIsLoadingReason(false)
    }
  }

  const closeRejectionModal = () => {
    setShowRejectionModal(false)
    setRejectionReason(null)
  }

  const handleRefresh = () => {
    refetch()
    toast.success(t('success'))
  }

  if (!isConnected) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <div className="w-14 h-14 bg-dark-800/50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <ArrowLeftRight className="w-7 h-7 text-dark-500" />
        </div>
        <h2 className="heading text-lg text-white mb-2">{t('connectWalletHistory')}</h2>
        <p className="text-dark-400 text-sm mb-4">{t('connectWalletHistoryDesc')}</p>
        <Link to="/" className="btn-primary btn-md">{t('goToBridge')}</Link>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="heading text-xl text-white mb-4">{t('history')}</h1>
        <div className="card p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-uni-400 mx-auto" />
          <p className="text-dark-400 mt-2">{t('loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="heading text-xl sm:text-2xl text-white">{t('history')}</h1>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          aria-label="Refrescar"
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-dark-800/50 hover:bg-dark-700/50 text-dark-400 hover:text-uni-400 transition-all disabled:opacity-50 shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {locks.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="w-14 h-14 bg-dark-800/50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <ArrowLeftRight className="w-7 h-7 text-dark-500" />
          </div>
          <h3 className="heading text-lg text-white mb-2">{t('noRequestsYet')}</h3>
          <p className="text-dark-400 text-sm mb-4">{t('noRequestsYetDesc')}</p>
          <Link to="/" className="btn-primary btn-md">{t('firstBridge')}</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {locks.map((lock) => {
            const statusConfig = getStatusConfig(lock.status)
            const StatusIcon = statusConfig.icon
            const isThisCancelling = cancellingLockId === lock.lockId

            return (
              <div key={lock.lockId.toString()} className="card p-4 hover:border-dark-700 transition-all">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Status */}
                    <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs', statusConfig.bgColor)}>
                      <StatusIcon className={cn('w-3.5 h-3.5', statusConfig.color)} />
                      <span className={statusConfig.color}>{statusConfig.label}</span>
                    </div>

                    {/* Amounts */}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-white">{parseFloat(lock.amountIn).toFixed(2)}</span>
                      <span className="text-dark-500">→</span>
                      <span className="font-mono text-accent-400">{parseFloat(lock.amountOut).toFixed(2)} USDT</span>
                    </div>

                    {/* Lock ID */}
                    <span className="text-xs text-dark-500 bg-dark-800/50 px-2 py-0.5 rounded">
                      #{lock.lockId.toString()}
                    </span>
                  </div>

                  {/* Right side: Links, Date, Cancel */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                    {/* Link to UNI2 transaction */}
                    {sourceTxHashes.get(lock.lockId.toString()) ? (
                      <a
                        href={`${UNI2_EXPLORER}/uni2/tx/${sourceTxHashes.get(lock.lockId.toString())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark-400 hover:text-uni-400 flex items-center gap-1"
                      >
                        {t('txUni2')} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-dark-500 flex items-center gap-1">
                        Uni2 <Loader2 className="w-3 h-3 animate-spin" />
                      </span>
                    )}
                    {lock.externalTxHash && lock.externalTxHash !== '0x0000000000000000000000000000000000000000000000000000000000000000' && (
                      <a
                        href={`${BSC_EXPLORER}/tx/${lock.externalTxHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark-400 hover:text-uni-400 flex items-center gap-1"
                      >
                        {t('txBsc')} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <span className="text-dark-500">
                      {lock.timestamp.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                        day: '2-digit',
                        month: 'short',
                      })}
                    </span>

                    {/* Cancel button for PENDING status only */}
                    {lock.status === LockStatus.PENDING && (
                      <button
                        onClick={() => openCancelModal(lock.lockId, lock.amountIn)}
                        disabled={isCancelling}
                        className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/15 border border-red-500/20 text-red-400 hover:bg-red-500/25 transition-all disabled:opacity-50 flex items-center gap-1"
                      >
                        {isThisCancelling ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {t('cancelling')}
                          </>
                        ) : (
                          t('cancel')
                        )}
                      </button>
                    )}

                    {/* View reason button for REJECTED status */}
                    {lock.status === LockStatus.REJECTED && (
                      <button
                        onClick={() => fetchRejectionReason(lock.lockId)}
                        disabled={isLoadingReason}
                        className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/15 border border-red-500/20 text-red-400 hover:bg-red-500/25 transition-all disabled:opacity-50 flex items-center gap-1"
                      >
                        {isLoadingReason ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Info className="w-3 h-3" />
                            {language === 'es' ? 'Ver motivo' : 'View reason'}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && lockToCancel && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card-glow p-6 max-w-sm w-full space-y-4 animate-in fade-in zoom-in-95 duration-200">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center">
              <h3 className="heading text-lg text-white mb-2">{t('confirmCancel')}</h3>
              <p className="text-dark-400 text-sm">
                {language === 'es'
                  ? `Se cancelará el bridge de ${parseFloat(lockToCancel.amount).toFixed(2)} W2USDT y los tokens serán devueltos a tu wallet.`
                  : `The bridge of ${parseFloat(lockToCancel.amount).toFixed(2)} W2USDT will be cancelled and tokens will be returned to your wallet.`
                }
              </p>
            </div>

            {/* Info box */}
            <div className="bg-dark-850/50 rounded-xl p-3 border border-dark-800/50">
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">Lock ID</span>
                <span className="font-mono text-white">#{lockToCancel.lockId.toString()}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-dark-400">{t('amount')}</span>
                <span className="font-mono text-white">{parseFloat(lockToCancel.amount).toFixed(2)} W2USDT</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeCancelModal}
                className="btn-secondary btn-md flex-1"
              >
                {language === 'es' ? 'No, mantener' : 'No, keep it'}
              </button>
              <button
                onClick={handleConfirmCancel}
                className="btn-md flex-1 bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-all"
              >
                {language === 'es' ? 'Sí, cancelar' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal after Cancellation */}
      {showSuccessModal && cancelledAmount && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card-glow p-6 max-w-sm w-full space-y-4 animate-in fade-in zoom-in-95 duration-200">
            {/* Success Icon */}
            <div className="flex justify-center">
              <div className="w-14 h-14 bg-accent-500/20 rounded-xl flex items-center justify-center animate-glow-pulse">
                <CheckCircle className="w-7 h-7 text-accent-400" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center">
              <h3 className="heading text-lg text-white mb-2">
                {language === 'es' ? 'Bridge Cancelado' : 'Bridge Cancelled'}
              </h3>
              <p className="text-dark-400 text-sm">
                {language === 'es'
                  ? `Se han devuelto ${parseFloat(cancelledAmount).toFixed(2)} W2USDT a tu wallet.`
                  : `${parseFloat(cancelledAmount).toFixed(2)} W2USDT have been returned to your wallet.`
                }
              </p>
            </div>

            {/* Info */}
            <div className="bg-accent-500/10 border border-accent-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 text-sm text-accent-400">
                <CheckCircle className="w-4 h-4" />
                <span>
                  {language === 'es'
                    ? 'Los tokens están de vuelta en tu wallet'
                    : 'Tokens are back in your wallet'
                  }
                </span>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={closeSuccessModal}
              className="btn-primary btn-md w-full"
            >
              {language === 'es' ? 'Entendido' : 'Got it'}
            </button>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectionModal && rejectionReason && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card-glow p-6 max-w-sm w-full space-y-4 animate-in fade-in zoom-in-95 duration-200">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center">
                <Ban className="w-7 h-7 text-red-400" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center">
              <h3 className="heading text-lg text-white mb-2">
                {language === 'es' ? 'Bridge Rechazado' : 'Bridge Rejected'}
              </h3>
              <p className="text-dark-400 text-sm">
                {language === 'es'
                  ? 'Tu solicitud de bridge ha sido rechazada por el siguiente motivo:'
                  : 'Your bridge request was rejected for the following reason:'
                }
              </p>
            </div>

            {/* Reason Box */}
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <p className="text-sm text-red-300">{rejectionReason}</p>
            </div>

            {/* Close Button */}
            <button
              onClick={closeRejectionModal}
              className="btn-secondary btn-md w-full"
            >
              {language === 'es' ? 'Entendido' : 'Got it'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
